// Abwesenheiten — Anträge stellen, genehmigen und den Teamkalender sehen.
//
// Drei Sichten in einer Seite: „Meine" (eigene Anträge + Urlaubskonto), „Genehmigen"
// (nur Personal/Geschäftsführung, offene Anträge zuerst) und „Team" (wer ist wann weg).
// Darstellung im Widget-Stil (design_handoff_personal §4); die Pille behält ihre Steuerung,
// die Steuerzeile der Seite nutzt dieselben Handler.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useIslandActions } from '../../../components/island/IslandProvider';
import { usePillenSteuerung } from '../../../components/island/widgets/SteuerungsAnsicht';
import type { SteuerungsEintrag } from '../../../components/island/widgets/typen';
import { AirplaneTilt, ArrowUUpLeft, CircleNotch, Plus, Rows, ShieldCheck, Stack } from '@phosphor-icons/react';
import { Segment } from '../../../components/ui/Segment';
import { GlasAuswahl } from '../../../components/ui/GlasAuswahl';
import { GlasTooltipSchicht } from '../../../components/ui/GlasTooltip';
import { useAppMeldungen } from '../../../components/ui/AppMeldungen';
import { useMobilModus } from '../../../lib/immobilien/mobil';
import { PersonalKopf, SteuerKnopf } from '../personal/teile/Rahmen';
import { AntragDialog, AntragKarte, LeerKarte, TeamKalender, UrlaubsKonto } from '../personal/teile/AbwesenheitTeile';
import {
  antragEntscheiden, antragStornieren, ladeAbwesenheiten, ladeMitarbeiter,
  ladeModelle, ladeZuordnungen, nachweisUrl,
  kommentarSchreiben, ladeGenehmigungsstufen, ladeVerlauf, setzeGenehmigungsstufen,
  type VerlaufEintrag,
} from '../../../lib/zeiterfassung/api';
import {
  TYP_INFO, urlaubskonto, urlaubsanspruch, zeitraumText, type Abwesenheit,
} from '../../../lib/zeiterfassung/abwesenheit';
import { sollMinutenAmTag, tagVon, type Arbeitszeitmodell, type Zuordnung } from '../../../lib/zeiterfassung/berechnung';
import { darfPersonalVerwalten } from '../../../lib/personal/rechte';

interface Props {
  userProfile?: { id?: string; role?: string; full_name?: string } | null;
}

const STUFEN_OPTIONEN = [
  { wert: '1', label: '1 — nur Personal/Geschäftsführung' },
  { wert: '2', label: '2 — Teamleitung, dann Personal' },
  { wert: '3', label: '3 — Teamleitung, Personal, Geschäftsführung' },
];

const AbwesenheitenSeite: React.FC<Props> = ({ userProfile }) => {
  const { notify } = useIslandActions();
  const { mobil } = useMobilModus();
  const { bestaetige, dialoge } = useAppMeldungen(mobil);
  const [antragOffen, setAntragOffen] = useState(false);
  const meineId = userProfile?.id || '';
  const darfEntscheiden = darfPersonalVerwalten(userProfile);

  const [sicht, setSicht] = useState<'meine' | 'genehmigen' | 'team'>('meine');
  const [alle, setAlle] = useState<Abwesenheit[]>([]);
  const [mitarbeiter, setMitarbeiter] = useState<{ id: string; full_name: string | null; email: string | null }[]>([]);
  const [modelle, setModelle] = useState<Arbeitszeitmodell[]>([]);
  const [zuordnungen, setZuordnungen] = useState<Zuordnung[]>([]);
  const [laedt, setLaedt] = useState(true);
  const [arbeitet, setArbeitet] = useState<string | null>(null);
  const [offenerVerlauf, setOffenerVerlauf] = useState<string | null>(null);
  const [verlauf, setVerlauf] = useState<VerlaufEintrag[]>([]);
  const [kommentar, setKommentar] = useState('');
  const [stufen, setStufen] = useState(1);

  const laden = useCallback(async () => {
    setLaedt(true);
    const [liste, leute, m, z] = await Promise.all([
      ladeAbwesenheiten(),
      ladeMitarbeiter(),
      ladeModelle(),
      meineId ? ladeZuordnungen(meineId) : Promise.resolve([]),
    ]);
    setAlle(liste);
    setMitarbeiter(leute);
    setModelle(m);
    setZuordnungen(z);
    setStufen(await ladeGenehmigungsstufen());
    setLaedt(false);
  }, [meineId]);

  useEffect(() => { void laden(); }, [laden]);

  const sollAmTag = useCallback(
    (tag: string) => sollMinutenAmTag(tag, zuordnungen, modelle),
    [zuordnungen, modelle],
  );

  const name = useCallback(
    (id: string) => mitarbeiter.find((m) => m.id === id)?.full_name || mitarbeiter.find((m) => m.id === id)?.email || 'Unbekannt',
    [mitarbeiter],
  );

  const meine = useMemo(() => alle.filter((a) => a.mitarbeiter_id === meineId), [alle, meineId]);
  const offene = useMemo(() => alle.filter((a) => a.status === 'offen'), [alle]);
  const konto = useMemo(() => {
    const jahr = new Date().getFullYear();
    return urlaubskonto(jahr, urlaubsanspruch(zuordnungen, modelle), meine, sollAmTag);
  }, [meine, zuordnungen, modelle, sollAmTag]);

  const entscheiden = async (a: Abwesenheit, status: 'genehmigt' | 'abgelehnt') => {
    setArbeitet(a.id);
    const fehler = await antragEntscheiden(a.id, status);
    setArbeitet(null);
    if (fehler) { notify({ title: `Nicht möglich: ${fehler}`, tone: 'error' }); return; }
    notify({ title: status === 'genehmigt' ? 'Antrag genehmigt' : 'Antrag abgelehnt', tone: 'success' });
    void laden();
  };

  const stornieren = async (a: Abwesenheit) => {
    if (!await bestaetige({ nachricht: `${TYP_INFO[a.typ].label} ${zeitraumText(a)} wirklich zurückziehen?`, label: 'Zurückziehen', icon: ArrowUUpLeft })) return;
    setArbeitet(a.id);
    await antragStornieren(a.id);
    setArbeitet(null);
    void laden();
  };

  const verlaufOeffnen = async (a: Abwesenheit) => {
    if (offenerVerlauf === a.id) { setOffenerVerlauf(null); return; }
    setOffenerVerlauf(a.id);
    setKommentar('');
    setVerlauf(await ladeVerlauf('abwesenheit', a.id));
  };

  const kommentieren = async (a: Abwesenheit) => {
    const fehler = await kommentarSchreiben('abwesenheit', a.id, meineId, kommentar);
    if (fehler) { notify({ title: fehler, tone: 'error' }); return; }
    setKommentar('');
    setVerlauf(await ladeVerlauf('abwesenheit', a.id));
  };

  const nachweisOeffnen = async (a: Abwesenheit) => {
    if (!a.nachweis_path) return;
    const url = await nachweisUrl(a.nachweis_path, a.nachweis_name);
    if (url) window.open(url, '_blank', 'noopener');
  };

  const stufenAendern = async (w: string) => {
    const wert = Number(w);
    const fehler = await setzeGenehmigungsstufen(wert);
    if (fehler) { notify({ title: fehler, tone: 'error' }); return; }
    setStufen(wert);
    notify({ title: 'Freigabestufen geändert', tone: 'success' });
  };

  usePillenSteuerung({
    titel: 'Abwesenheiten',
    eintraege: [
      { id: 'antrag', typ: 'aktion', farbe: 'gold', icon: AirplaneTilt, kicker: 'Antrag', titel: 'Antrag stellen', unter: 'Urlaub oder Abwesenheit',
        onClick: () => setAntragOffen(true) },
      ...(darfEntscheiden
        ? [{ id: 'stufen', typ: 'auswahl', farbe: 'violett', icon: ShieldCheck, kicker: 'Freigabestufen', wert: String(stufen), unter: 'Freigabe-Workflow',
            optionen: STUFEN_OPTIONEN,
            onChange: (w: string) => { void stufenAendern(w); } } satisfies SteuerungsEintrag]
        : []),
      { id: 'sicht', typ: 'segment', farbe: 'blau', icon: Rows, kicker: 'Ansicht', titel: 'Sicht', voll: true, wert: sicht,
        onChange: (w: string) => setSicht(w as typeof sicht),
        optionen: [
          { wert: 'meine', label: 'Meine' },
          ...(darfEntscheiden ? [{ wert: 'genehmigen', label: `Offen (${offene.length})` }] : []),
          { wert: 'team', label: 'Team' },
        ] },
    ],
  }, [sicht, offene.length, darfEntscheiden, meineId, sollAmTag, stufen],);

  // Teamkalender: die nächsten 28 Tage als Raster
  const teamTage = useMemo(() => {
    const start = new Date();
    return Array.from({ length: 28 }, (_, i) => {
      const d = new Date(start.getTime() + i * 86400000);
      return tagVon(d.toISOString());
    });
  }, []);

  const liste = sicht === 'genehmigen' ? offene : meine;

  return (
    <div className="flex flex-col" style={{ gap: 14 }}>
      <PersonalKopf bereich="abwesenheiten" verwalten={darfEntscheiden} mobil={mobil} />

      {/* Steuerzeile — dieselben Handler wie die Pille */}
      <div className="flex flex-wrap items-center" style={{ gap: 10 }}>
        <Segment<'meine' | 'genehmigen' | 'team'> ariaLabel="Sicht" hoehe={mobil ? 44 : 34} wert={sicht} onChange={setSicht}
          eintraege={[
            { key: 'meine', label: 'Meine', tip: 'Meine – Eigene Anträge und Urlaubskonto' },
            ...(darfEntscheiden ? [{ key: 'genehmigen' as const, label: `Offen (${offene.length})`, tip: 'Offen – Anträge, die auf eine Entscheidung warten' }] : []),
            { key: 'team', label: 'Team', tip: 'Team – Wer ist in den nächsten vier Wochen weg' },
          ]} />
        {darfEntscheiden && (
          <GlasAuswahl wert={String(stufen)} onChange={(w) => { void stufenAendern(w); }} icon={Stack} ariaLabel="Freigabestufen"
            tip="Freigabestufen – Wie viele Stellen einen Antrag nacheinander genehmigen" style={{ maxWidth: '100%' }} optionen={STUFEN_OPTIONEN} />
        )}
        <div className="flex" style={{ marginLeft: mobil ? 0 : 'auto' }}>
          <SteuerKnopf icon={Plus} primaer onClick={() => setAntragOffen(true)} tip="Antrag stellen – Urlaub, Krankheit, Homeoffice und mehr beantragen">
            Antrag stellen
          </SteuerKnopf>
        </div>
      </div>

      {sicht === 'meine' && <UrlaubsKonto konto={konto} mobil={mobil} />}

      {laedt ? (
        <div className="flex items-center justify-center" style={{ padding: 48, color: 'var(--ink3)' }}>
          <CircleNotch size={22} className="animate-spin" />
        </div>
      ) : sicht === 'team' ? (
        <TeamKalender mitarbeiter={mitarbeiter} alle={alle} tage={teamTage} heute={teamTage[0]} />
      ) : liste.length === 0 ? (
        <LeerKarte text={sicht === 'genehmigen' ? 'Keine offenen Anträge' : 'Noch keine Abwesenheiten'} />
      ) : (
        <div className="flex flex-col" style={{ gap: 10 }}>
          {liste.map((a) => (
            <AntragKarte key={a.id} a={a} mitName={sicht === 'genehmigen'} name={name} stufen={stufen} sollAmTag={sollAmTag}
              darfEntscheiden={darfEntscheiden} meineId={meineId} arbeitet={arbeitet === a.id}
              verlaufOffen={offenerVerlauf === a.id} verlauf={verlauf} kommentar={kommentar} onKommentar={setKommentar}
              onVerlauf={() => { void verlaufOeffnen(a); }} onKommentieren={() => { void kommentieren(a); }}
              onEntscheiden={(st) => { void entscheiden(a, st); }} onStornieren={() => { void stornieren(a); }}
              onNachweis={() => { void nachweisOeffnen(a); }} />
          ))}
        </div>
      )}

      {antragOffen && (
        <AntragDialog mitarbeiterId={meineId} sollAmTag={sollAmTag} onAbbrechen={() => setAntragOffen(false)}
          onFertig={() => { setAntragOffen(false); void laden(); }} />
      )}
      {dialoge}
      <GlasTooltipSchicht />
    </div>
  );
};

export default AbwesenheitenSeite;
